import Api from "@ciq/shared/api/api"

/**
 * @typedef {Object} FileInput
 * @property {string} filename - Original filename
 * @property {string} file_type - File type (pdf, png, jpg)
 * @property {string|null} [building_zone_id] - Building zone ID (optional)
 */

/**
 * @typedef {Object} UploadInfo
 * @property {string} filename - Original filename
 * @property {string} file_type - File type
 * @property {string} upload_url - Pre-signed S3 URL for upload
 * @property {string} s3_key - S3 key where file will be stored
 */

/**
 * @typedef {Object} CreateJobResponse
 * @property {string} job_id - Job ID
 * @property {string} status - Job status (PENDING)
 * @property {UploadInfo[]} uploads - Upload URLs for each file
 */

/**
 * Create a floor plan upload job and get presigned URLs for all files.
 * @param {string} schoolId - School ID
 * @param {FileInput[]} files - Array of file metadata
 * @returns {Promise<CreateJobResponse>}
 */
export async function createFloorPlanUploadJob(schoolId, files) {
  const response = await Api.post(`/schools/${schoolId}/floor-plan-uploads/jobs`, { files })
  return response.data
}

/**
 * Upload file directly to S3 using pre-signed URL.
 * Uses XMLHttpRequest for upload progress tracking.
 * @param {string} uploadUrl - Pre-signed S3 URL
 * @param {File} file - File to upload
 * @param {(percent: number) => void} [onProgress] - Progress callback (0-100)
 * @param {AbortSignal} [signal] - AbortSignal for cancellation on unmount
 * @returns {Promise<void>}
 */
export function uploadFileToS3(uploadUrl, file, onProgress, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Upload aborted", "AbortError"))
      return
    }

    const xhr = new XMLHttpRequest()

    const onAbort = () => {
      xhr.abort()
      reject(new DOMException("Upload aborted", "AbortError"))
    }
    signal?.addEventListener("abort", onAbort)

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100)
        onProgress(percent)
      }
    })

    xhr.addEventListener("load", () => {
      signal?.removeEventListener("abort", onAbort)
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error("Upload failed: server rejected the file. Please try again."))
      }
    })

    xhr.addEventListener("error", () => {
      signal?.removeEventListener("abort", onAbort)
      reject(new Error("Upload failed: network error. Please check your connection and try again."))
    })

    xhr.addEventListener("timeout", () => {
      signal?.removeEventListener("abort", onAbort)
      reject(new Error("Upload failed: request timed out. Please check your connection and try again."))
    })

    xhr.open("PUT", uploadUrl)
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream")
    xhr.timeout = 300000
    xhr.send(file)
  })
}

/**
 * Submit a floor plan upload job for processing after all files are uploaded to S3.
 * This triggers the backend to process files and create file records.
 * @param {string} schoolId - School ID
 * @param {string} jobId - Job ID from createFloorPlanUploadJob
 * @returns {Promise<{job_id: string, status: string}>}
 */
export async function submitFloorPlanJob(schoolId, jobId) {
  const response = await Api.post(`/schools/${schoolId}/floor-plan-uploads/jobs/${jobId}/submit`)
  return response.data
}

/**
 * Get floor plan job status.
 * @param {string} schoolId - School ID
 * @param {string} jobId - Job ID
 * @returns {Promise<{job_id: string, status: string}>}
 */
export async function getFloorPlanJobStatus(schoolId, jobId) {
  const response = await Api.get(`/schools/${schoolId}/floor-plan-uploads/jobs/${jobId}`)
  return response.data
}

const TERMINAL_STATUSES = ["COMPLETED", "ERROR"]
const POLL_INTERVAL_MS = 2000
const MAX_POLL_ATTEMPTS = 30

/**
 * Poll job status until it reaches a terminal state (COMPLETED or ERROR).
 * @param {string} schoolId - School ID
 * @param {string} jobId - Job ID
 * @param {AbortSignal} [signal] - AbortSignal for cancellation on unmount
 * @returns {Promise<Object>} Final job status
 */
export async function pollJobUntilComplete(schoolId, jobId, signal) {
  for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
    if (signal?.aborted) {
      throw new DOMException("Polling aborted", "AbortError")
    }
    const status = await getFloorPlanJobStatus(schoolId, jobId)
    if (TERMINAL_STATUSES.includes(status.status)) {
      return status
    }
    await new Promise(r => setTimeout(r, POLL_INTERVAL_MS))
  }
  throw new Error("Upload processing timed out. Please check the file list and try again if needed.")
}
